import React, { useState } from 'react';

const PackageForm = ({ packageName, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    packageName: packageName || "",
    comments: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // For subsequent submissions, use AJAX
      const response = await fetch('https://formsubmit.co/ajax/aa21f22bd858af2b66556ae63df9e108', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          subject: `Package Request: ${formData.packageName}`,
          message: `
            Package: ${formData.packageName}
            Name: ${formData.fullName}
            Email: ${formData.email}
            Phone: ${formData.phone || 'Not provided'}
            Comments: ${formData.comments || 'No comments provided'}
            
            This user has requested information about the ${formData.packageName} package.
          `,
          _template: "table",
          _captcha: "false"
        })
      });
      
      if (response.ok) {
        setSubmitStatus({ 
          success: true, 
          message: "Thank you for your interest! We've received your request and will reach out shortly to schedule a video call." 
        });
        // Reset form
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          packageName: packageName || "",
          comments: "",
        });
        
        // Close modal after 3 seconds on success
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setSubmitStatus({ 
        success: false, 
        message: "There was a problem sending your request. Please try again or contact us directly." 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col max-h-[calc(100vh-4rem)] overflow-hidden">
      {submitStatus && (
        <div className={`p-4 mb-6 rounded-md flex-shrink-0 ${submitStatus.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {submitStatus.message}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="flex flex-col h-full overflow-hidden">
        {/* Scrollable form content */}
        <div className="flex-1 overflow-y-auto px-1 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Selected Package</label>
            <input
              type="text"
              name="packageName"
              value={formData.packageName}
              readOnly
              className="w-full px-3 py-2 border border-purple/20 rounded-md bg-gray-50 text-gray-500 focus:outline-none"
            />
          </div>
          
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name*</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Your full name"
              className={`w-full px-3 py-2 border ${errors.fullName ? 'border-red-500' : 'border-purple/20'} rounded-md focus:outline-none focus:border-purple`}
            />
            {errors.fullName && (
              <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
            )}
          </div>
          
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Your email address"
              className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-purple/20'} rounded-md focus:outline-none focus:border-purple`}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Your phone number (optional)"
              className="w-full px-3 py-2 border border-purple/20 rounded-md focus:outline-none focus:border-purple"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              placeholder="Any additional comments or questions? (optional)"
              rows="4"
              className="w-full px-3 py-2 border border-purple/20 rounded-md focus:outline-none focus:border-purple resize-none"
            />
          </div>
        </div>

        {/* Fixed button section at bottom with extra mobile padding */}
        <div className="flex justify-end pt-4 mt-2 border-t border-gray-200 flex-shrink-0 pb-16 sm:pb-0">
          <button
            type="button"
            onClick={onClose}
            className="mr-3 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="home-two-btn-bg py-2 px-6 group bg-[#4A7DFF] border-[#4A7DFF] inline-flex disabled:opacity-70 disabled:cursor-not-allowed rounded-md"
          >
            <span className="text-base text-white group-hover:text-[#4A7DFF] transition-all duration-300 font-inter relative z-10">
              {isSubmitting ? "Sending..." : "Submit Request"}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default PackageForm; 